import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { hotComics, newComics, comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import type { Comic } from '@/mocks/comics';

const heroSlides = [
  {
    image: 'https://readdy.ai/api/search-image?query=Epic%20Korean%20manhwa%20style%20digital%20illustration%2C%20fantasy%20warrior%20standing%20on%20a%20cliff%20overlooking%20a%20magical%20kingdom%2C%20dramatic%20sunset%20lighting%2C%20cinematic%20composition%2C%20anime%20inspired%20art%2C%20vibrant%20warm%20golden%20and%20orange%20tones%2C%20wide%20panoramic%20view%2C%20atmospheric%20depth%2C%20professional%20webtoon%20cover%20quality%2C%20detailed%20environment%20art%2C%20epic%20scale&width=1920&height=900&seq=hero-slide-01&orientation=landscape',
    tag: '🔥 今日热门',
    title: '探索韩国漫画的无限世界',
    subtitle: '最新最全的正版韩漫，每日更新，高清画质，尽在韩漫大全',
    link: '/category/fantasy',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Romantic%20Korean%20manhwa%20style%20illustration%20of%20a%20couple%20walking%20under%20cherry%20blossom%20trees%20at%20sunset%2C%20soft%20pastel%20colors%2C%20dreamy%20atmosphere%2C%20cinematic%20lighting%2C%20anime%20inspired%20art%2C%20warm%20pink%20and%20golden%20tones%2C%20wide%20landscape%20composition%2C%20professional%20webtoon%20quality&width=1920&height=900&seq=hero-slide-02&orientation=landscape',
    tag: '💕 恋爱推荐',
    title: '甜蜜浪漫，心动不已',
    subtitle: '数百部精选恋爱韩漫，温暖治愈你的每一天',
    link: '/category/romance',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Action%20packed%20Korean%20manhwa%20illustration%2C%20dynamic%20fight%20scene%20in%20a%20futuristic%20city%2C%20explosions%20and%20energy%20effects%2C%20cinematic%20lighting%2C%20anime%20inspired%20digital%20art%2C%20intense%20red%20and%20orange%20tones%2C%20wide%20heroic%20composition%2C%20professional%20webtoon%20cover%20quality&width=1920&height=900&seq=hero-slide-03&orientation=landscape',
    tag: '⚔️ 热血推荐',
    title: '燃烧热血，激燃战斗',
    subtitle: '顶级动作韩漫，每一页都燃到爆炸',
    link: '/category/action',
  },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const hotItems: Comic[] = hotComics.slice(0, 3);
  const newItems: Comic[] = newComics.slice(0, 8);
  const recItems: Comic[] = comics.slice(3, 9);

  return (
    <>
      <Navbar />
      <main>
        <section className="relative h-[420px] md:h-[600px] overflow-hidden" aria-label="首页轮播横幅">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <span className="inline-block border border-white/40 text-white text-xs font-medium px-3 py-1 rounded-md mb-4">
                  {heroSlides[currentSlide].tag}
                </span>
                <h1 className="text-3xl md:text-5xl font-bold text-white font-heading leading-tight mb-4">
                  {heroSlides[currentSlide].title}
                </h1>
                <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg">
                  {heroSlides[currentSlide].subtitle}
                </p>
                <Link
                  to={heroSlides[currentSlide].link}
                  className="inline-flex items-center gap-2 bg-primary-500 text-white dark:text-foreground-950 text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  立即探索
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  index === currentSlide ? 'bg-white w-6' : 'bg-white/40'
                }`}
                aria-label={`切换到第 ${index + 1} 张`}
              ></button>
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6" aria-label="热门推荐">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              🔥 热门推荐
            </h2>
            <p className="text-foreground-500 text-sm mt-2">千万读者都在追的韩漫神作</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
            {hotItems.map((comic) => (
              <ComicCard key={comic.id} comic={comic} variant="featured" />
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100" aria-label="漫画分类">
          <div className="mb-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              探索分类
            </h2>
            <p className="text-foreground-500 text-sm mt-2">找到你最喜欢的漫画类型</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-lg p-5 hover:bg-primary-500 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg mb-3 group-hover:bg-white/20 transition-colors">
                  <i className={`${cat.icon} text-lg text-secondary-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-white dark:group-hover:text-foreground-950 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 mt-1 group-hover:text-white/70 dark:group-hover:text-foreground-950/70 transition-colors">
                  {cat.description}
                </p>
                <p className="text-xs text-foreground-400 mt-2 group-hover:text-white/50 dark:group-hover:text-foreground-950/50 transition-colors">
                  {cat.count} 部作品
                </p>
              </Link>
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6" aria-label="最新上架">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                🆕 最新上架
              </h2>
              <p className="text-foreground-500 text-sm mt-2">刚出炉的热乎韩漫，趁热看</p>
            </div>
            <Link
              to="/category/romance"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {newItems.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100" aria-label="为您推荐">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              ✨ 为您推荐
            </h2>
            <p className="text-foreground-500 text-sm mt-2">根据您的喜好精选优质韩漫</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
            {recItems.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden" aria-label="CTA区域">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20characters%20reading%20comics%20together%2C%20warm%20cozy%20atmosphere%2C%20soft%20lighting%2C%20anime%20inspired%20art%2C%20gentle%20pastel%20colors%2C%20wide%20panoramic%20composition%2C%20comfortable%20reading%20scene&width=1920&height=600&seq=cta-bg-home&orientation=landscape"
            className="absolute inset-0 w-full h-full object-cover object-top"
            alt=""
          />
          <div className="absolute inset-0 bg-accent-950/85"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">
              准备好开始你的韩漫之旅了吗？
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8">
              数千部正版韩国漫画等你来探索，全部免费阅读，每日更新不重样
            </p>
            <Link
              to="/category/fantasy"
              className="inline-flex items-center gap-2 bg-primary-500 text-white dark:text-foreground-950 text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即开始阅读
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '韩漫大全',
            url: 'https://www.example.com/',
            description: '最新最全的正版韩国漫画在线阅读平台，每日更新，高清画质，涵盖各种题材。',
            inLanguage: 'zh-CN',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.example.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '韩漫大全',
            url: 'https://www.example.com/',
            description: '韩国漫画免费在线阅读平台',
            sameAs: ['http://caishenlicai.com/'],
          }),
        }}
      />
    </>
  );
}